import React, { Component, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Functional component for translated error UI
function CrispErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('common');
  
  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md">
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t('errors.chatUnavailable', 'Chat Temporarily Unavailable')}</AlertTitle>
        <AlertDescription className="mt-2 space-y-2">
          <p className="text-sm">
            {t('errors.chatLoadError', "We're having trouble loading the chat widget. This doesn't affect the rest of the application.")}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onRetry}
              className="gap-2"
            >
              <RefreshCw className="h-3 w-3" />
              {t('buttons.retry', 'Retry')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              asChild
            >
              <a href="mailto:support@mindcohealth.com">
                {t('buttons.contactSupport', 'Contact Support')}
              </a>
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

export class CrispErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log Crisp-specific errors
    console.error('Crisp Error Boundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    // Clear Crisp-related localStorage to force fresh initialization
    try {
      localStorage.removeItem('crisp_workspace_id');
    } catch (e) {
      console.warn('Could not clear Crisp localStorage:', e);
    }

    // Reset error state and reload the page to reinitialize Crisp
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return <CrispErrorFallback onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}
